import { useEffect, useState, useCallback } from "react";
import Dashboard from "../components/Dashboard";
import Pagination from "../components/Pagination";
import P2PResolveSidebar, {
  S,
  triggerP2PAgent1,
} from "../components/P2PResolveSidebar";
import {
  ObjectPageHeader,
  PageContainer,
  FilterBar,
  Field,
  Select,
  SearchInput,
  FioriButton,
  FioriSwitch,
  StatusPill,
  ResponsiveTable,
  Spinner,
  EmptyState,
  Dialog,
  IconButton,
} from "../components/FioriUI";
import { config } from "../config";

// ─── Endpoints ────────────────────────────────────────────────────────────────
const P2P_URL     = "/api/p2p/exceptions";
const WEBHOOK_URL = config.p2p.webhookUrl;
const POLL_URL    = "/api/webhook/request";
const API_TOKEN   = config.p2p.apiToken;

const POLL_INTERVAL_MS  = 3000;
const POLL_MAX_ATTEMPTS = 30;

// ─── localStorage cache ───────────────────────────────────────────────────────
// Stores { [exception_id]: { agent1Result, agent2Result } }
const CACHE_KEY = "p2p_agent_cache";
const SIDEBAR_STATE_KEY = "p2p_sidebar_state";

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); } catch { return {}; }
}

function saveToCache(exceptionId, patch) {
  try {
    const cache = readCache();
    cache[exceptionId] = { ...(cache[exceptionId] || {}), ...patch };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {}
}

function saveSidebarState(exceptionId, phase) {
  try {
    localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify({ exceptionId, phase }));
  } catch {}
}

function loadSidebarState() {
  try {
    return JSON.parse(localStorage.getItem(SIDEBAR_STATE_KEY) || "null");
  } catch {
    return null;
  }
}

function clearSidebarState() {
  try {
    localStorage.removeItem(SIDEBAR_STATE_KEY);
  } catch {}
}

function clearCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch {}
}

// ─── Webhook helper ───────────────────────────────────────────────────────────
export async function triggerAndPoll(inputMessage) {
  const triggerRes = await fetch(WEBHOOK_URL, {
    method:  "POST",
    headers: { "Content-Type": "application/json", "x-api-token": API_TOKEN },
    body:    JSON.stringify({ files: [], input_message: inputMessage, metadata: {} }),
  });

  if (!triggerRes.ok) throw new Error(`Webhook trigger failed: ${triggerRes.status}`);
  const { request_id } = await triggerRes.json();
  if (!request_id) throw new Error("No request_id in webhook response");

  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

    const pollRes = await fetch(`${POLL_URL}/${request_id}`, {
      headers: { "x-api-token": API_TOKEN },
    });
    if (!pollRes.ok) continue;

    const data   = await pollRes.json();
    const status = data?.status;

    if (status === "failed") throw new Error(data?.response || "Agent failed");

    if (status === "success") {
      const finalAnswer = data?.full_response?.response?.results?.[0]?.final_answer;
      const fallback    = data?.response || "";
      console.log("[triggerAndPoll] final_answer:", finalAnswer);
      console.log("[triggerAndPoll] fallback:", fallback);

      const raw = finalAnswer || fallback;
      if (!raw) return { raw_response: "" };
      if (typeof raw === "object") return raw;

      const stripped = raw
        .replace(/^```json\s*/i, "")
        .replace(/^```\s*/,      "")
        .replace(/\s*```$/,      "")
        .trim();

      try { return JSON.parse(stripped); }
      catch {
        console.warn("[triggerAndPoll] JSON parse failed");
        return { raw_response: stripped };
      }
    }
  }
  throw new Error("Agent timed out after 90 seconds. Please refresh and try again.");
}

// ─────────────────────────────────────────────────────────────────────────────
function P2PExceptions() {
  const [data,         setData]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [filters,      setFilters]      = useState({
    status: "", exceptionType: "", search: "", vendor: "", processedTime: "",
  });

  const [showWarning,    setShowWarning]    = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [rowToRefresh,   setRowToRefresh]   = useState(null);
  const [rowLoading,     setRowLoading]     = useState(null);

  // In-memory mirror of localStorage cache
  const [agentCache, setAgentCache] = useState(() => readCache());

  // Sidebar state — lifted, matching M2C pattern
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [sidebarRow,    setSidebarRow]    = useState(null);
  const [activeCaseId,  setActiveCaseId]  = useState("");
  const [agentPhase,    setAgentPhase]    = useState(S.IDLE);

  // Save sidebar state when phase or case changes
  useEffect(() => {
    if (sidebarOpen && activeCaseId) {
      saveSidebarState(activeCaseId, agentPhase);
    }
  }, [sidebarOpen, activeCaseId, agentPhase]);

  // Restore sidebar state on mount
  useEffect(() => {
    const saved = loadSidebarState();
    if (saved && saved.exceptionId) {
      setActiveCaseId(saved.exceptionId);
      setAgentPhase(saved.phase || S.IDLE);
      setSidebarOpen(true);
      // Find the row data
      const row = data.find(r => r.exception_id === saved.exceptionId);
      if (row) setSidebarRow(row);
    }
  }, [data]);
  const [agent1Result,  setAgent1Result]  = useState(null);
  const [agent2Result,  setAgent2Result]  = useState(null);

  const [vendorList,        setVendorList]        = useState([]);
  const [exceptionTypeList, setExceptionTypeList] = useState([]);
  const [expandedActions,   setExpandedActions]   = useState({});
  const [view,              setView]              = useState("");
  const isDashboard = view === "dashboard";
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // ── Helpers ────────────────────────────────────────────────────────────────
  const persistAndSetCache = (exceptionId, patch) => {
    saveToCache(exceptionId, patch);
    setAgentCache((prev) => ({
      ...prev,
      [exceptionId]: { ...(prev[exceptionId] || {}), ...patch },
    }));
  };

  // ── Data load ──────────────────────────────────────────────────────────────
  const fetchData = useCallback(() => {
    setLoading(true);
    fetch(P2P_URL)
      .then((r) => r.json())
      .then((json) => {
        const apiData = (json.total_exceptions_in_db || []).filter(
          (item) => item.exception_category === "procure_to_pay",
        );
        setData(apiData);
        setFilteredData(apiData);
        setExceptionTypeList(Array.from(new Set(apiData.map((i) => i.exception_name).filter(Boolean))));
        setVendorList(Array.from(new Set(apiData.map((i) => i.vendor_name).filter(Boolean))));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Filtering ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let f = [...data];
    if (filters.status)        f = f.filter((i) => (i.status || "").toUpperCase() === filters.status.toUpperCase());
    if (filters.exceptionType) f = f.filter((i) => i.exception_name === filters.exceptionType);
    if (filters.vendor)        f = f.filter((i) => i.vendor_name === filters.vendor);
    if (filters.search) {
      const s = filters.search.toLowerCase();
      f = f.filter((i) =>
        i.document_number?.toLowerCase().includes(s) ||
        i.vendor_name?.toLowerCase().includes(s) ||
        i.exception_id?.toLowerCase().includes(s) ||
        i.exception_name?.toLowerCase().includes(s),
      );
    }
    setFilteredData(f);
  }, [data, filters]);

  useEffect(() => { setCurrentPage(1); }, [filters]);

  const totalPages    = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = filteredData.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const getValue           = (row, key) => row[key] || "-";
  // Dates render in UTC and short form, consistent with the trail panel.
  const fmtDate = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    return isNaN(d) ? "-" : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
  };
  const handleFilterChange = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const handleClearFilters = () =>
    setFilters({ status: "", exceptionType: "", search: "", vendor: "", processedTime: "" });

  const toggleActions = (rowId) =>
    setExpandedActions((prev) => ({ ...prev, [rowId]: !prev[rowId] }));

  const renderActionDetails = (actionDetails) => {
    if (!actionDetails) return <span className="text-fiori-text-disabled">-</span>;

    let parts = [];
    if (Array.isArray(actionDetails)) {
      parts = actionDetails
        .map((item) => (typeof item === "string" ? item : item.description || item.agent_action || JSON.stringify(item)))
        .map((s) => s.replace(/^[•\-]\s*/, "").trim())
        .filter((s) => s.length > 0);
    } else if (typeof actionDetails === "string") {
      parts = actionDetails
        .split(/[•\n]/)
        .map((s) => s.replace(/^-\s*/, "").trim())
        .filter((s) => s.length > 0);
    } else {
      return <span className="text-fiori-text-disabled">-</span>;
    }

    if (parts.length === 0) return <span className="text-fiori-text-disabled">-</span>;

    const waitingRegex = /waiting for|pending/i;
    return (
      <div className="space-y-2">
        {parts.map((line, idx) => {
          const isWaiting = waitingRegex.test(line);
          return (
            <div
              key={idx}
              className={`text-sm leading-snug ${isWaiting ? "text-fiori-warning font-medium" : "text-fiori-success font-medium"}`}
            >
              • {line}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Resolve — Agent 1 ──────────────────────────────────────────────────────
  const handleResolve = async (exceptionId) => {
    const row = filteredData.find((i) => i.exception_id === exceptionId);
    if (row?.action_needed === false) {
      setWarningMessage("Resolution is already in process, please check back later.");
      setShowWarning(true);
      return;
    }

    // Open sidebar immediately with loading state
    // Set exception_name to null so agent can determine it
    const rowWithNullType = { ...row, exception_name: null };
    setSidebarRow(rowWithNullType);
    setActiveCaseId(exceptionId);
    setAgent1Result(null);
    setAgent2Result(null);
    setAgentPhase(S.AGENT1_LOADING);
    setSidebarOpen(true);

    setRowLoading(exceptionId);
    try {
      const agent1 = await triggerP2PAgent1(exceptionId);
      console.log("[Agent 1 result]", agent1);

      // Persist agent1Result to cache
      persistAndSetCache(exceptionId, { agent1Result: agent1, agent2Result: null });

      // Refresh row in table
      await refreshRow(exceptionId);

      setAgent1Result(agent1);
      setAgentPhase(S.AGENT1_DONE);
    } catch (err) {
      setAgentPhase(S.AGENT1_ERROR);
      setWarningMessage(err.message || "Error running agent. Please try again.");
      setShowWarning(true);
    } finally {
      setRowLoading(null);
    }
  };

  // ── Row refresh ────────────────────────────────────────────────────────────
  const refreshRow = async (exceptionId) => {
    try {
      const res     = await fetch(P2P_URL);
      const json    = await res.json();
      const apiData = (json.total_exceptions_in_db || []).filter(
        (item) => item.exception_category === "procure_to_pay",
      );
      const updated = apiData.find((item) => item.exception_id === exceptionId);
      if (updated) {
        setData((prev) => prev.map((item) => item.exception_id === exceptionId ? updated : item));
        setFilteredData((prev) => prev.map((item) => item.exception_id === exceptionId ? updated : item));
      }
    } catch (_) {}
  };

  const handleWarningClose = async () => {
    setShowWarning(false);
    if (rowToRefresh) { await refreshRow(rowToRefresh); setRowToRefresh(null); }
  };

  // ── Eye icon → open sidebar with cached data ───────────────────────────────
  const handleViewDetails = (row) => {
    const cached = agentCache[row.exception_id] || {};
    setSidebarRow(row);
    setActiveCaseId(row.exception_id);
    setAgent1Result(cached.agent1Result || null);
    setAgent2Result(cached.agent2Result || null);
    setAgentPhase(cached.agent1Result ? S.AGENT1_DONE : S.IDLE);
    setSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
    clearSidebarState();
  };

  // ── Status change callback from sidebar ─────────────────────────────────────
  const handleStatusChange = (exceptionId, newStatus) => {
    setData((prev) =>
      prev.map((item) =>
        item.exception_id === exceptionId ? { ...item, status: newStatus } : item
      )
    );
    setFilteredData((prev) =>
      prev.map((item) =>
        item.exception_id === exceptionId ? { ...item, status: newStatus } : item
      )
    );
    refreshRow(exceptionId);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <ObjectPageHeader
        title="P2P Exceptions Management"
        subtitle="Procure to Pay exceptions awaiting your action"
        actions={
          <>
            <FioriSwitch
              checked={isDashboard}
              onChange={(v) => setView(v ? "dashboard" : "")}
              label="Dashboard"
            />
            <button
              type="button"
              onClick={() => window.open("/sop-editor?doc=p2p", "_blank")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-fiori-border-dark rounded-fiori text-fiori-brand bg-transparent hover:bg-fiori-brand/5 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className="w-7 h-7">
                <g transform="translate(1.407 1.407) scale(2.81 2.81)">
                  <path d="M24.731 57.127v-4.129c0-.444-.361-.806-.806-.806H20.05v5.74h3.876c.444 0 .805-.362.805-.805z"/>
                  <path d="M39.841 63.508V53.286c0-.604-.491-1.094-1.094-1.094H35.16v12.41h3.587c.604 0 1.094-.492 1.094-1.094z"/>
                  <path d="M81.979 17.233L66.215 1.469A5.013 5.013 0 0062.67 0H20.59c-2.764 0-5.013 2.249-5.013 5.013v36.168h-4.28c-2.617 0-4.745 2.084-4.745 4.646v25.139c0 2.563 2.128 4.646 4.745 4.646h4.28v9.374c0 2.764 2.249 5.013 5.013 5.013h57.844c2.764 0 5.013-2.249 5.013-5.013v-64.21a5.013 5.013 0 00-1.468-3.543zM18.05 68.603c-1.104 0-2-.896-2-2v-6.67v-9.74c0-1.104.896-2 2-2h5.876c2.65 0 4.806 2.156 4.806 4.806v4.129c0 2.649-2.156 4.806-4.806 4.806H20.05v4.67c0 1.103-.896 1.999-2 1.999zM79.447 84.987c0 .559-.454 1.013-1.013 1.013H20.59c-.559 0-1.013-.454-1.013-1.013v-9.374h44.425c2.616 0 4.745-2.084 4.745-4.646V45.828c0-2.562-2.129-4.646-4.745-4.646H19.578V5.013C19.578 4.454 20.032 4 20.59 4h41.328v12.784c0 2.617 2.129 4.745 4.745 4.745h12.784v63.458zM31.16 66.603v-16.41c0-1.104.896-2 2-2h5.587c2.809 0 5.094 2.285 5.094 5.094v10.222c0 2.81-2.285 5.095-5.094 5.095H33.16c-1.105 0-2-.896-2-2zM54.255 56.397c1.104 0 2 .896 2 2s-.896 2-2 2h-3.686v6.205c0 1.104-.896 2-2 2s-2-.896-2-2v-16.41c0-1.104.896-2 2-2h8.682c1.104 0 2 .896 2 2s-.896 2-2 2h-6.682v4.205h3.686z"/>
                </g>
              </svg>
              SOP
            </button>
            <FioriButton variant="emphasized" onClick={fetchData} disabled={loading} loading={loading}>
              {loading ? "Refreshing" : "Refresh"}
            </FioriButton>
          </>
        }
      />

      <PageContainer>
        {/* ── KPI cards ── */}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {(() => {
              const totalCases      = filteredData.length;
              const resolvedCases   = filteredData.filter((i) => (i.status || "").toUpperCase() === "RESOLVED").length;
              const inProgressCases = filteredData.filter((i) => (i.status || "").toUpperCase() === "IN_PROGRESS").length;
              const uniqueTypes     = new Set(filteredData.map((i) => i.exception_name).filter(Boolean)).size;
              const resolutionRate  = totalCases > 0 ? ((resolvedCases / totalCases) * 100).toFixed(1) : "0.0";
              return [
                { label: "TOTAL CASES",     value: totalCases },
                { label: "RESOLVED",        value: resolvedCases, subtitle: `${resolutionRate}% resolution rate` },
                { label: "IN PROGRESS",     value: inProgressCases },
                { label: "EXCEPTION TYPES", value: uniqueTypes },
                { label: "AI ACCURACY",     value: `${resolutionRate}%` },
              ].map((card) => (
                <div key={card.label} className="bg-white rounded-lg border border-gray-200 shadow-sm px-5 py-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{card.label}</p>
                  <p className="text-3xl font-bold text-gray-900 leading-tight">{card.value}</p>
                  {card.subtitle && <p className="text-xs text-gray-400 mt-1">{card.subtitle}</p>}
                </div>
              ));
            })()}
          </div>
        )}

        {/* ── Filters ── */}
        <FilterBar title="Filter Invoices" onClear={handleClearFilters}>
          <Field label="Status" htmlFor="status">
            <Select id="status" value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)}>
              <option value="">All</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="RESOLVED">Resolved</option>
            </Select>
          </Field>
          <Field label="Exception Type" htmlFor="reasonCode">
            <Select id="reasonCode" value={filters.exceptionType} onChange={(e) => handleFilterChange("exceptionType", e.target.value)}>
              <option value="">All</option>
              <option value="Currency Mismatch">Currency Mismatch</option>
              <option value="Late invoicing">Late Invoicing</option>
              {exceptionTypeList.map((t) => <option key={t} value={t}>{t.toLowerCase().replace(/\b\w/g, char => char.toUpperCase())}</option>)}
            </Select>
          </Field>
          <Field label="Vendor" htmlFor="vendor">
            <Select id="vendor" value={filters.vendor} onChange={(e) => handleFilterChange("vendor", e.target.value)}>
              <option value="">All</option>
              {vendorList.map((v) => <option key={v} value={v}>{v}</option>)}
            </Select>
          </Field>
          <Field label="Search" htmlFor="p2p-search">
            <SearchInput
              id="p2p-search"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              placeholder="Search Invoices…"
            />
          </Field>
        </FilterBar>

        {/* ── Dashboard or Table ── */}
        {isDashboard ? (
          <Dashboard filteredData={filteredData} source="p2p" />
        ) : (
          <>
            <div className="flex justify-end mb-3">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
            <ResponsiveTable>
              <thead>
                <tr className="!bg-fiori-brand">
                  <th className="!bg-fiori-brand !text-white !text-center !px-3 !py-3 whitespace-nowrap" style={{width:"11%"}}>Invoice</th>
                  <th className="!bg-fiori-brand !text-white !text-center !px-3 !py-3 whitespace-nowrap" style={{width:"11%"}}>Vendor</th>
                  <th className="!bg-fiori-brand !text-white !text-center !px-3 !py-3 whitespace-nowrap" style={{width:"9%"}}>Amount</th>
                  <th className="!bg-fiori-brand !text-white !text-center !px-3 !py-3 whitespace-nowrap" style={{width:"11%"}}>Exception Type</th>
                  <th className="!bg-fiori-brand !text-white !text-center !px-3 !py-3 whitespace-nowrap" style={{width:"22%"}}>Agent Actions</th>
                  <th className="!bg-fiori-brand !text-white !text-center !px-3 !py-3 whitespace-nowrap" style={{width:"9%"}}>Invoice Received</th>
                  <th className="!bg-fiori-brand !text-white !text-center !px-3 !py-3 whitespace-nowrap" style={{width:"9%"}}>Last Action</th>
                  <th className="!bg-fiori-brand !text-white !text-center !px-3 !py-3 whitespace-nowrap" style={{width:"9%"}}>Status</th>
                  <th className="!bg-fiori-brand !text-white !text-center !px-3 !py-3 whitespace-nowrap" style={{width:"8%"}}>Details</th>
   
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9}>
                    <div className="flex justify-center items-center gap-3 py-8 text-fiori-text-secondary text-base">
                      <Spinner /> Loading exceptions
                    </div>
                  </td></tr>
                ) : !Array.isArray(filteredData) || filteredData.length === 0 ? (
                  <tr><td colSpan={9}>
                    <EmptyState title="No exceptions" description="No items match the current filters." />
                  </td></tr>
                ) : (
                  paginatedData.map((row, idx) => {
                    const statusUpper = (row.status || "").toUpperCase();
                    const showDetails = !["OPEN", "INITIATED"].includes(statusUpper);
                    const isRunning   = rowLoading === row.exception_id;
                    return (
                      <tr key={idx} className="border-b border-fiori-border-light hover:bg-gray-50 transition-colors">
                        <td className="font-semibold text-base whitespace-nowrap py-5 px-6">
                          {getValue(row, "document_number")}
                        </td>
                        <td className="text-base py-5 px-6 whitespace-nowrap">
                          {getValue(row, "vendor_name")}
                        </td>
                        <td className="text-base py-5 px-6 whitespace-nowrap">
                          <span>USD </span>
                          <span className="font-mono">{getValue(row, "amount")}</span>
                        </td>
                        <td className="text-base py-5 px-6 whitespace-nowrap">
                          {getValue(row, "exception_name")}
                        </td>
                        <td className="py-5 px-6 min-w-[280px]">
                          {(() => {
                            const notes = row.notes;
                            if (!notes) return <span className="text-fiori-text-disabled">-</span>;
                            
                            // Parse notes if it's a JSON string
                            let parsedNotes = [];
                            try {
                              const parsed = typeof notes === 'string' ? JSON.parse(notes) : notes;
                              if (Array.isArray(parsed)) {
                                parsedNotes = parsed.map(item => item.text || item).filter(Boolean);
                              } else if (typeof parsed === 'string') {
                                parsedNotes = [parsed];
                              } else {
                                parsedNotes = [notes];
                              }
                            } catch {
                              // If parsing fails, treat as plain string
                              parsedNotes = [notes];
                            }
                            
                            if (parsedNotes.length === 0) return <span className="text-fiori-text-disabled">-</span>;
                            
                            const isExpanded = expandedActions[row.exception_id];
                            const fullText = parsedNotes.join(' • ');
                            const shouldTruncate = fullText.length > 100;
                            
                            return (
                              <div className="border border-fiori-border-light rounded-fiori p-2 bg-fiori-page-2/40 max-w-md">
                                <div className={!isExpanded && shouldTruncate ? "overflow-hidden max-h-[42px]" : ""}>
                                  {parsedNotes.map((note, idx) => (
                                    <p key={idx} className="text-sm leading-snug text-fiori-text mb-1 last:mb-0">
                                      {parsedNotes.length > 1 ? '• ' : ''}{note}
                                    </p>
                                  ))}
                                </div>
                                {shouldTruncate && (
                                  <button
                                    type="button"
                                    onClick={() => toggleActions(row.exception_id)}
                                    className="text-sm font-semibold text-fiori-brand hover:underline mt-1"
                                  >
                                    {isExpanded ? "Show less" : "Show more"}
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="text-base py-5 px-6 whitespace-nowrap text-center text-fiori-text-secondary">
                          {fmtDate(row.invoice_received_date)}
                        </td>
                        <td className="text-base py-5 px-6 whitespace-nowrap text-center text-fiori-text-secondary">
                          {fmtDate(row.last_action_date)}
                        </td>
                        <td className="py-5 px-6 whitespace-nowrap">
                          <StatusPill status={row.status} />
                        </td>
                        <td className="text-center py-5 px-6">
                          {showDetails ? (
                            <IconButton title="View Details" onClick={() => handleViewDetails(row)}>
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12C2.25 12 5.25 6.75 12 6.75C18.75 6.75 21.75 12 21.75 12C21.75 12 18.75 17.25 12 17.25C5.25 17.25 2.25 12 2.25 12Z"/>
                                <circle cx="12" cy="12" r="3"/>
                              </svg>
                            </IconButton>
                          ) : null}
                        </td>
                        
                      </tr>
                    );
                  })
                )}
              </tbody>
            </ResponsiveTable>
          </>
        )}
      </PageContainer>

      {/* ── Warning dialog ── */}
      <Dialog
        show={showWarning}
        title="Agent Update"
        size="sm"
        onClose={handleWarningClose}
        footer={<FioriButton variant="emphasized" onClick={handleWarningClose}>OK</FioriButton>}
      >
        <div className="px-4 py-5 text-sm text-fiori-text">{warningMessage}</div>
      </Dialog>

      {/* ── P2P ResolveSidebar — always mounted, slides in/out ── */}
      <P2PResolveSidebar
        rowData={sidebarRow}
        isOpen={sidebarOpen}
        onClose={handleSidebarClose}
        onStatusChange={handleStatusChange}
        activeCaseId={activeCaseId}
        phase={agentPhase}
        setPhase={setAgentPhase}
        agent1={agent1Result}
        setAgent1={setAgent1Result}
        agent2={agent2Result}
        setAgent2={setAgent2Result}
      />
    </>
  );
}

export default P2PExceptions;
